import { useEffect, useMemo, useState } from 'react'
import { useDeviceDetectorScan, useFoundDevices } from 'src/Libs/installations/hooks/installation-configuration/storage/use-device-detector'
import { FoundDeviceModel } from 'src/Libs/installations/models/device-detector.model'
import { isPowerMeterFoundDevice } from 'src/Libs/installations/utils/device-detector'

interface UsePowerMeterScanDialogParams {
  onClose?: () => void
  emcSerialNumber: string
  onSelect: (serialNumber: string, ipAddress?: string, index?: number) => void
}

interface UsePowerMeterScanDialogResult {
  foundPowerMeters: FoundDeviceModel[]
  selectedSerialNumber: string | null
  selectSerialNumber: (serialNumber: string) => void
  hasScanError: boolean
  isLoading: boolean
  isScanning: boolean
  hasScanResults: boolean
  handleCancel: () => void
  handleRescan: () => void
  handleConfirm: () => void
}

/**
 * Drives the power meter scan dialog. On open it shows the results of the last completed
 * device detector scan by reusing the found-devices query already loaded for the power meter
 * form (see usePowerMeterLastScanIpAddress), so opening the dialog does not trigger another
 * found-devices request on its own. A new scan is only initiated once the user explicitly
 * requests a rescan.
 */
export const usePowerMeterScanDialog = ({ onClose, emcSerialNumber, onSelect }: UsePowerMeterScanDialogParams): UsePowerMeterScanDialogResult => {
  const [selectedSerialNumber, setSelectedSerialNumber] = useState<string | null>(null)
  const [isRescanTriggered, setIsRescanTriggered] = useState(false)

  const { data: lastScanData, isLoading: isLoadingLastScan, isError: hasLastScanError } = useFoundDevices(emcSerialNumber, { refetchOnMount: false })

  const {
    scanStatus,
    scanResult,
    isScanning,
    hasCompleted: hasRescanCompleted,
    error: rescanError,
    startScan,
    stopScan,
  } = useDeviceDetectorScan(emcSerialNumber)

  const hasRescanError = (rescanError !== null && !isScanning && !hasRescanCompleted) || scanStatus?.status === 'error'

  const devices = isRescanTriggered ? scanResult?.devices : lastScanData?.devices
  const foundPowerMeters = useMemo(() => (devices ?? []).filter(isPowerMeterFoundDevice), [devices])

  const hasScanError = isRescanTriggered ? hasRescanError : hasLastScanError
  const hasScanResults = isRescanTriggered ? hasRescanCompleted && !hasRescanError : !isLoadingLastScan && !hasLastScanError

  useEffect(() => {
    return () => stopScan()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCancel = (): void => {
    stopScan()
    onClose?.()
  }

  const handleRescan = (): void => {
    setSelectedSerialNumber(null)
    setIsRescanTriggered(true)
    void startScan()
  }

  const handleConfirm = (): void => {
    if (!selectedSerialNumber) return
    const selectedDevice = foundPowerMeters.find(device => device.serialNumber === selectedSerialNumber)
    onSelect(selectedSerialNumber, selectedDevice?.ipAddress, selectedDevice?.index)
    handleCancel()
  }

  return {
    foundPowerMeters,
    selectedSerialNumber,
    selectSerialNumber: setSelectedSerialNumber,
    hasScanError,
    isLoading: isRescanTriggered ? false : isLoadingLastScan,
    isScanning,
    hasScanResults,
    handleCancel,
    handleRescan,
    handleConfirm,
  }
}
